import React from 'react';
import { motion } from 'framer-motion';
import { SmartphoneIcon, GlobeIcon, PhoneIcon, MailIcon, MessageSquareIcon, SparklesIcon, UsersIcon, CopyIcon, FileSearchIcon, SearchIcon, ShuffleIcon, RefreshCwIcon, CircleIcon, FileTextIcon, DatabaseIcon, MonitorIcon, CloudIcon, ClockIcon, AlertTriangleIcon, XCircleIcon, EyeOffIcon, ArrowRightIcon, ArrowDownIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { ProblemEvidence } from './ProblemEvidence';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.5,
    ease: EASE
  }
};
type Bilingual = {
  en: string;
  fr: string;
};
type Item = {
  icon: typeof SmartphoneIcon;
  title: Bilingual;
  desc: Bilingual;
};
const CHANNELS: Item[] = [{
  icon: SmartphoneIcon,
  title: {
    fr: 'Application mobile',
    en: 'Mobile app'
  },
  desc: {
    fr: 'Demandes et formulaires',
    en: 'Requests and forms'
  }
}, {
  icon: GlobeIcon,
  title: {
    fr: 'Site web',
    en: 'Website'
  },
  desc: {
    fr: 'Formulaires et services',
    en: 'Forms and services'
  }
}, {
  icon: PhoneIcon,
  title: {
    fr: 'Téléphone',
    en: 'Phone'
  },
  desc: {
    fr: 'Centre d’appels',
    en: 'Call centre'
  }
}, {
  icon: MailIcon,
  title: {
    fr: 'Courriel',
    en: 'Email'
  },
  desc: {
    fr: 'Demandes entrantes',
    en: 'Incoming requests'
  }
}, {
  icon: MessageSquareIcon,
  title: {
    fr: 'Réseaux sociaux',
    en: 'Social media'
  },
  desc: {
    fr: 'Messages et commentaires',
    en: 'Messages and comments'
  }
}, {
  icon: SparklesIcon,
  title: {
    fr: 'BCITI | ATNA',
    en: 'BCITI | ATNA'
  },
  desc: {
    fr: 'Comprend et oriente',
    en: 'Understands and routes'
  }
}];
const TASKS: Item[] = [{
  icon: CopyIcon,
  title: {
    fr: 'Ressaisir',
    en: 'Re-enter'
  },
  desc: {
    fr: 'les informations',
    en: 'information'
  }
}, {
  icon: FileSearchIcon,
  title: {
    fr: 'Vérifier',
    en: 'Verify'
  },
  desc: {
    fr: 'les doublons',
    en: 'duplicates'
  }
}, {
  icon: SearchIcon,
  title: {
    fr: 'Chercher',
    en: 'Search'
  },
  desc: {
    fr: 'dans les systèmes',
    en: 'across systems'
  }
}, {
  icon: ShuffleIcon,
  title: {
    fr: 'Transférer',
    en: 'Transfer'
  },
  desc: {
    fr: 'aux bonnes équipes',
    en: 'to the right teams'
  }
}, {
  icon: RefreshCwIcon,
  title: {
    fr: 'Réconcilier',
    en: 'Reconcile'
  },
  desc: {
    fr: 'les informations',
    en: 'information'
  }
}, {
  icon: CircleIcon,
  title: {
    fr: 'Relancer',
    en: 'Follow up'
  },
  desc: {
    fr: 'les demandeurs',
    en: 'on requests'
  }
}];
const SYSTEMS: Item[] = [{
  icon: FileTextIcon,
  title: {
    fr: 'Gestion des requêtes',
    en: 'Request management'
  },
  desc: {
    fr: 'Traitement et suivi',
    en: 'Processing and tracking'
  }
}, {
  icon: DatabaseIcon,
  title: {
    fr: 'CRM / ERP',
    en: 'CRM / ERP'
  },
  desc: {
    fr: 'Données et dossiers',
    en: 'Data and records'
  }
}, {
  icon: MonitorIcon,
  title: {
    fr: 'Systèmes opérationnels',
    en: 'Operational systems'
  },
  desc: {
    fr: 'Opérations et équipes terrain',
    en: 'Operations and field teams'
  }
}, {
  icon: UsersIcon,
  title: {
    fr: 'Services internes',
    en: 'Internal services'
  },
  desc: {
    fr: 'Services et processus',
    en: 'Departments and processes'
  }
}, {
  icon: CloudIcon,
  title: {
    fr: 'Autres systèmes',
    en: 'Other systems'
  },
  desc: {
    fr: 'Partenaires et applications',
    en: 'Partners and applications'
  }
}];
const OUTCOMES: Item[] = [{
  icon: ClockIcon,
  title: {
    fr: 'Temps perdu',
    en: 'Time lost'
  },
  desc: {
    fr: 'à faire le lien',
    en: 'connecting systems'
  }
}, {
  icon: CopyIcon,
  title: {
    fr: 'Doublons',
    en: 'Duplicates'
  },
  desc: {
    fr: 'la même demande plusieurs fois',
    en: 'the same request multiple times'
  }
}, {
  icon: AlertTriangleIcon,
  title: {
    fr: 'Délais plus longs',
    en: 'Longer delays'
  },
  desc: {
    fr: 'entre les équipes',
    en: 'between teams'
  }
}, {
  icon: XCircleIcon,
  title: {
    fr: 'Erreurs',
    en: 'Errors'
  },
  desc: {
    fr: 'informations incomplètes ou incohérentes',
    en: 'incomplete or inconsistent information'
  }
}, {
  icon: EyeOffIcon,
  title: {
    fr: 'Peu de visibilité',
    en: 'Limited visibility'
  },
  desc: {
    fr: 'aucune vue d’ensemble',
    en: 'no big-picture view'
  }
}];

// fragmented system → outcome links (index pairs)
const RED_LINKS: [number, number][] = [[0, 1], [0, 3], [1, 0], [1, 2], [1, 4], [2, 1], [2, 3], [3, 0], [3, 4], [4, 2], [4, 3]];
function ColumnHeading({
  title,
  sub
}: {
  title: string;
  sub: string;
}) {
  return <div data-id="element-6509" data-mp="1j70v3n68z8of">
      <p className="font-sans text-[12px] font-semibold tracking-[0.2em] uppercase text-[#5B6478]" data-id="element-6510" data-mp="1j8lt5mrrt9r1">{title}</p>
      <p className="font-sans text-[14px] text-[#8A90A0] mt-1.5" data-id="element-6511" data-mp="virgb57gbl6s">{sub}</p>
    </div>;
}
function RowCard({
  item,
  pick,
  tone
}: {
  item: Item;
  pick: (v: Bilingual) => string;
  tone: 'indigo' | 'gray';
}) {
  const Icon = item.icon;
  return <li className="flex-1 flex items-center gap-4 rounded-xl border border-[#E8E9EE] bg-white px-4 py-3 min-h-[60px]" data-id="element-6512" data-mp="10frmdg4awnxw">
      <span className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${tone === 'indigo' ? 'bg-[#F4F4FC]' : ''}`} data-id="element-6513" data-mp="2fvgmlosx3s46">
        <Icon className={`w-5 h-5 ${tone === 'indigo' ? 'text-[#4F46E5]' : 'text-[#5B6478]'}`} strokeWidth={1.6} aria-hidden="true" data-id="element-6514" data-mp="2bx96mw6a2uu1" />
      </span>
      <span className="min-w-0" data-id="element-6515" data-mp="2oe24sj93yopa">
        <span className="block font-sans text-[14.5px] text-[#121212] leading-tight" data-id="element-6516" data-mp="2ofd68cf2zkd8">{pick(item.title)}</span>
        <span className="block font-sans text-[12px] text-[#8A90A0] leading-snug mt-1" data-id="element-6517" data-mp="29im7m5d8ecoo">{pick(item.desc)}</span>
      </span>
    </li>;
}
function OutcomeCard({
  item,
  pick
}: {
  item: Item;
  pick: (v: Bilingual) => string;
}) {
  const Icon = item.icon;
  return <li className="flex-1 flex items-start gap-3.5 rounded-xl border border-[#F6CFCB] bg-[#FDF6F5] px-4 py-3 min-h-[60px]" data-id="element-6518" data-mp="3738fpk3nuc0j">
      <span className="w-8 h-8 shrink-0 rounded-full bg-[#FBE7E4] flex items-center justify-center" data-id="element-6519" data-mp="2seud9aa5opki">
        <Icon className="w-4 h-4 text-[#D6453D]" strokeWidth={1.8} aria-hidden="true" data-id="element-6520" data-mp="2xcnle8v0rtnf" />
      </span>
      <span className="min-w-0" data-id="element-6521" data-mp="3l2q9kdvtcfgr">
        <span className="block font-sans text-[14.5px] text-[#C23A33] leading-tight" data-id="element-6522" data-mp="3puripqxopq2k">{pick(item.title)}</span>
        <span className="block font-sans text-[12px] text-[#8A90A0] leading-snug mt-1" data-id="element-6523" data-mp="3l3zpnp2wibo">{pick(item.desc)}</span>
      </span>
    </li>;
}
function MobileDown() {
  return <div className="lg:hidden flex justify-center py-5" aria-hidden="true" data-id="element-6524" data-mp="3upu9c332mg9d">
      <ArrowDownIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-6525" data-mp="3qo8nvtch0tnk" />
    </div>;
}
export function ScatteredRequests() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  const t = (fr: string, en: string) => isFR ? fr : en;
  const center = (i: number, n: number) => (i + 0.5) / n * 100;
  return <div className="max-w-[1440px] mx-auto" data-id="element-6526" data-mp="371b1tprui7ic">
      {/* Intro */}
      <motion.div {...reveal} className="text-center max-w-4xl mx-auto" data-id="element-6527" data-mp="330hcqt2o2mnv">
        <p className="font-sans text-[12px] font-semibold tracking-[0.24em] uppercase text-[#4F46E5]" data-id="element-6528" data-mp="332by28huogsh">
          {t('Le problème', 'The problem')}
        </p>
        <h2 className="font-serif text-[2.4rem] md:text-[3.2rem] lg:text-[3.6rem] text-[#121212] leading-[1.08] tracking-tight mt-6" data-id="element-6529" data-mp="2t21kunvtf9db">
          {t('Vos systèmes fonctionnent.', 'Your systems work.')}
          <span className="block" data-id="element-6530" data-mp="1dikdmj9yl29o">{t('Mais ils ne travaillent pas ensemble.', 'But they don’t work together.')}</span>
        </h2>
        <p className="font-sans text-[17px] md:text-[19px] text-[#5B6478] leading-relaxed mt-6" data-id="element-6531" data-mp="1iiyhrq8fvs2f">
          {t('Les demandes arrivent par plusieurs canaux et traversent plusieurs systèmes. Entre les deux, vos équipes doivent encore ressaisir, vérifier, chercher, transférer et relancer.', 'Requests arrive through multiple channels and move across multiple systems. In between, your teams still have to re-enter, verify, search, transfer and follow up.')}
        </p>
        <p className="font-sans text-[15px] text-[#8A90A0] mt-5" data-id="element-6532" data-mp="13mh58voik7ea">
          {t('La complexité se retrouve chez vos équipes.', 'The complexity ends up with your teams.')}
        </p>
      </motion.div>

      {/* Diagram */}
      <motion.div {...reveal} transition={{
      duration: 0.5,
      delay: 0.1,
      ease: EASE
    }} className="mt-16 grid grid-cols-1 lg:grid-cols-[minmax(0,23fr)_64px_minmax(0,31fr)_48px_minmax(0,20fr)_64px_minmax(0,20fr)] lg:grid-rows-[auto_1fr] lg:gap-y-4" data-id="element-6533" data-mp="b2imzzakuiv2">
        {/* Headings (desktop row 1) */}
        <div className="hidden lg:block lg:col-start-1 lg:row-start-1" data-id="element-6534" data-mp="35mba4f43vmhz">
          <ColumnHeading title={t('Les demandes arrivent', 'Requests come in')} sub={t('Plusieurs canaux. Un volume croissant.', 'Multiple channels. One growing volume.')} data-id="element-6535" data-mp="35oe7xlbi2jx9" />
        </div>
        <div className="hidden lg:block lg:col-start-3 lg:row-start-1 pl-4" data-id="element-6536" data-mp="3emg5fqw0yufs">
          <ColumnHeading title={t('Vos équipes font le lien', 'Your teams connect the dots')} sub={t('Encore trop de tâches manuelles.', 'Too many manual tasks.')} data-id="element-6537" data-mp="3taazbalrr4ug" />
        </div>
        <div className="hidden lg:block lg:col-start-5 lg:row-start-1" data-id="element-6538" data-mp="664py7nncqn4">
          <ColumnHeading title={t('Les systèmes restent séparés', 'Systems remain separate')} sub={t('Plusieurs outils. Peu de connexion.', 'Multiple tools. Limited connection.')} data-id="element-6539" data-mp="25kvxcyde357" />
        </div>
        <div className="hidden lg:block lg:col-start-7 lg:row-start-1" data-id="element-6540" data-mp="pspyin5gdlfx">
          <ColumnHeading title={t('Des résultats fragmentés', 'Fragmented outcomes')} sub={t('Plus de temps. Plus de risques.', 'More time. More risk.')} data-id="element-6541" data-mp="pqlt20rwoedj" />
        </div>

        {/* Column 1 — channels */}
        <div className="lg:col-start-1 lg:row-start-2 flex flex-col" data-id="element-6542" data-mp="gu4d8k8ljsbg">
          <div className="lg:hidden mb-4" data-id="element-6543" data-mp="21kgq2fycj2z">
            <ColumnHeading title={t('Les demandes arrivent', 'Requests come in')} sub={t('Plusieurs canaux. Un volume croissant.', 'Multiple channels. One growing volume.')} data-id="element-6544" data-mp="6tu2r7dy4yzz" />
          </div>
          <ul className="flex-1 flex flex-col gap-2" data-id="element-6545" data-mp="3p60befo6cg9o">
            {CHANNELS.map(c => <RowCard key={c.title.en} item={c} pick={pick} tone="indigo" data-id="element-6546" data-mp="3t72cl21nj2ea" />)}
          </ul>
        </div>

        {/* Connector: channels → team */}
        <div className="hidden lg:block lg:col-start-2 lg:row-start-2 relative" aria-hidden="true" data-id="element-6547" data-mp="3k7fasf50fukp">
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" fill="none" data-id="element-6548" data-mp="2uhi5lj41voes">
            {CHANNELS.map((_, i) => {
            const y = center(i, CHANNELS.length);
            return <path key={i} d={`M0 ${y} C 45 ${y}, 45 50, 88 50`} stroke="#A5A1EE" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-6549" data-mp="2kmgeihq7sc9k" data-mpi={i} />;
          })}
          </svg>
          <ArrowRightIcon className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-6550" data-mp="3i9ymvrfyrcd1" />
        </div>
        <MobileDown data-id="element-6551" data-mp="38hsyerox7d2l" />

        {/* Column 2 — teams */}
        <div className="lg:col-start-3 lg:row-start-2 flex flex-col lg:px-4" data-id="element-6552" data-mp="38g82axmyjm2m">
          <div className="lg:hidden mb-4" data-id="element-6553" data-mp="1hb4maaw4zd">
            <ColumnHeading title={t('Vos équipes font le lien', 'Your teams connect the dots')} sub={t('Encore trop de tâches manuelles.', 'Too many manual tasks.')} data-id="element-6554" data-mp="fxca1wh4dy" />
          </div>
          <div className="flex-1 flex flex-col justify-center rounded-2xl border border-[#E0DEF7] bg-[#F7F6FE] p-4 lg:p-5" data-id="element-6555" data-mp="1pha407hn0ea4">
            <div className="text-center" data-id="element-6556" data-mp="1ybor2w8est3s">
              <span className="mx-auto w-11 h-11 rounded-full bg-[#ECEBFC] flex items-center justify-center" data-id="element-6557" data-mp="1yaes84tqfgw3">
                <UsersIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-6558" data-mp="1uamteju54p3x" />
              </span>
              <p className="font-serif text-[1.45rem] text-[#121212] mt-3" data-id="element-6559" data-mp="16mmrqovajeak">{t('Vos équipes', 'Your teams')}</p>
              <p className="font-sans text-[13px] text-[#5B6478] mt-1" data-id="element-6560" data-mp="11nsahrcz1xsk">
                {t('Doivent faire le lien entre les canaux et les systèmes.', 'Still connecting channels and systems manually.')}
              </p>
            </div>
            <ul className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-6561" data-mp="2qzkmlxm8seyz">
              {TASKS.map(({
              icon: Icon,
              title,
              desc
            }) => <li key={title.en} className="rounded-xl bg-white px-2 py-4 text-center" data-id="element-6562" data-mp="2m10zvuztqz5k">
                  <span className="mx-auto w-9 h-9 rounded-full bg-[#F4F4FC] flex items-center justify-center" data-id="element-6563" data-mp="1jncvpfr6kdlb">
                    <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-6564" data-mp="1ol4pkfbdy9s5" />
                  </span>
                  <span className="block font-sans text-[14px] text-[#121212] mt-2.5" data-id="element-6565" data-mp="1xnnncl0bdw7j">{pick(title)}</span>
                  <span className="block font-sans text-[11.5px] text-[#9AA0AE] mt-0.5" data-id="element-6566" data-mp="10xd8kyewh8kj">{pick(desc)}</span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Arrow: team → systems */}
        <div className="hidden lg:flex lg:col-start-4 lg:row-start-2 items-center justify-center" aria-hidden="true" data-id="element-6567" data-mp="19xta42c325mr">
          <ArrowRightIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.8} data-id="element-6568" data-mp="1epjqoq1m5pmm" />
        </div>
        <MobileDown data-id="element-6569" data-mp="h8mepc7xdw5q" />

        {/* Column 3 — systems */}
        <div className="lg:col-start-5 lg:row-start-2 flex flex-col" data-id="element-6570" data-mp="m87efoux68ji">
          <div className="lg:hidden mb-4" data-id="element-6571" data-mp="m6e6m68kli9x">
            <ColumnHeading title={t('Les systèmes restent séparés', 'Systems remain separate')} sub={t('Plusieurs outils. Peu de connexion.', 'Multiple tools. Limited connection.')} data-id="element-6572" data-mp="ephmlb85iw7y" />
          </div>
          <ul className="flex-1 flex flex-col gap-2.5" data-id="element-6573" data-mp="4q8uknwujro8">
            {SYSTEMS.map(s => <RowCard key={s.title.en} item={s} pick={pick} tone="gray" data-id="element-6574" data-mp="pq2qtuyelm1" />)}
          </ul>
        </div>

        {/* Fragmented connectors: systems → outcomes */}
        <div className="hidden lg:block lg:col-start-6 lg:row-start-2 relative" aria-hidden="true" data-id="element-6575" data-mp="3rxb1he2wmsjh">
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" fill="none" data-id="element-6576" data-mp="3n2yfoxdyh54k">
            {RED_LINKS.map(([a, b]) => {
            const y1 = center(a, SYSTEMS.length);
            const y2 = center(b, OUTCOMES.length);
            return <path key={`${a}-${b}`} d={`M0 ${y1} C 55 ${y1}, 45 ${y2}, 100 ${y2}`} stroke="#F2A7A1" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-6577" data-mp="396t9jbpwc0z6" />;
          })}
          </svg>
        </div>
        <MobileDown data-id="element-6578" data-mp="yd7hug1w10tu" />

        {/* Column 4 — outcomes */}
        <div className="lg:col-start-7 lg:row-start-2 flex flex-col" data-id="element-6579" data-mp="ycpcmtx8zc9s">
          <div className="lg:hidden mb-4" data-id="element-6580" data-mp="13b0n96vd9qt2">
            <ColumnHeading title={t('Des résultats fragmentés', 'Fragmented outcomes')} sub={t('Plus de temps. Plus de risques.', 'More time. More risk.')} data-id="element-6581" data-mp="139y09a1608if" />
          </div>
          <ul className="flex-1 flex flex-col gap-2.5" data-id="element-6582" data-mp="tffs9soptqst">
            {OUTCOMES.map(o => <OutcomeCard key={o.title.en} item={o} pick={pick} data-id="element-6583" data-mp="ffeoa4d5m394" />)}
          </ul>
        </div>
      </motion.div>

      <ProblemEvidence data-id="element-6584" data-mp="kfakhhfm0qe4" />
    </div>;
}